import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowLeft, DollarSign, TrendingUp, Trophy } from 'lucide-react';
import { useLanguage } from '../i18n/LanguageContext';
import { setPageMeta } from '../lib/setPageMeta';
export function Competitors() {
  const {
    t
  } = useLanguage();
  const competitors = t('competitorsPage.competitors') as any[];
  useEffect(() => {
    setPageMeta({
      title: 'Competitors & Revenue',
      description: 'See how the biggest fan-subscription platforms stack up — and where Dance for Fans fits in.'
    });
  }, []);
  return <div className="min-h-screen font-sans" style={{
    backgroundColor: '#0a1f12',
    color: '#e8f5e9'
  }} data-id="element-218">
      {/* Money-green ambient lighting */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden" data-id="element-219">
        <motion.div className="absolute top-[-20%] left-[10%] w-[700px] h-[700px] rounded-full blur-3xl" style={{
        backgroundColor: 'rgba(34, 197, 94, 0.12)'
      }} animate={{
        scale: [1, 1.15, 1],
        x: [0, 40, 0]
      }} transition={{
        duration: 14,
        repeat: Infinity,
        ease: 'easeInOut'
      }} data-id="element-220" />
        <motion.div className="absolute bottom-[-20%] right-[5%] w-[600px] h-[600px] rounded-full blur-3xl" style={{
        backgroundColor: 'rgba(16, 185, 129, 0.15)'
      }} animate={{
        scale: [1, 1.2, 1],
        y: [0, -50, 0]
      }} transition={{
        duration: 16,
        repeat: Infinity,
        ease: 'easeInOut'
      }} data-id="element-221" />
      </div>

      <div className="relative z-10" data-id="element-222">
        {/* Top bar */}
        <header className="px-6 py-6 border-b" style={{
        borderColor: 'rgba(232, 245, 233, 0.1)'
      }} data-id="element-223">
          <div className="max-w-5xl mx-auto flex items-center justify-between" data-id="element-224">
            <Link to="/" className="inline-flex items-center gap-2 text-sm hover:opacity-80 transition-opacity" style={{
            color: '#e8f5e9'
          }} data-id="element-225">
              <ArrowLeft className="w-4 h-4" data-id="element-226" />
              {t('common.backToMain')}
            </Link>
            <span className="text-xs uppercase tracking-[0.3em]" style={{
            color: '#86efac'
          }} data-id="element-227">
              {t('competitorsPage.headerTag')}
            </span>
          </div>
        </header>

        {/* Hero */}
        <section className="px-6 py-24 md:py-32" data-id="element-228">
          <div className="max-w-4xl mx-auto text-center" data-id="element-229">
            <motion.p initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6
          }} className="text-sm uppercase tracking-[0.4em] mb-6" style={{
            color: '#86efac'
          }} data-id="element-230">
              {t('competitorsPage.heroTag')}
            </motion.p>

            <motion.h1 initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.7,
            delay: 0.1
          }} className="font-serif italic text-5xl md:text-7xl lg:text-8xl leading-[1.05] mb-8" style={{
            color: '#e8f5e9'
          }} data-id="element-231">
              {t('competitorsPage.heroTitleStart')}
              <span style={{
              color: '#22c55e'
            }} data-id="element-232">
                {t('competitorsPage.heroTitleHighlight')}
              </span>
              {t('competitorsPage.heroTitleEnd')}
            </motion.h1>

            <motion.p initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.7,
            delay: 0.2
          }} className="text-lg md:text-xl max-w-2xl mx-auto leading-relaxed" style={{
            color: 'rgba(232, 245, 233, 0.7)'
          }} data-id="element-233">
              {t('competitorsPage.heroDesc')}
            </motion.p>
          </div>
        </section>

        {/* Incumbent */}
        <section className="px-6 pb-16" data-id="element-234">
          <div className="max-w-5xl mx-auto" data-id="element-235">
            <motion.div initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-50px'
          }} transition={{
            duration: 0.7
          }} className="rounded-3xl p-8 md:p-12 border backdrop-blur-sm" style={{
            backgroundColor: 'rgba(20, 60, 35, 0.6)',
            borderColor: 'rgba(34, 197, 94, 0.4)'
          }} data-id="element-236">
              <div className="flex items-center gap-3 mb-6" data-id="element-237">
                <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{
                backgroundColor: 'rgba(34, 197, 94, 0.2)'
              }} data-id="element-238">
                  <Trophy className="w-6 h-6" style={{
                  color: '#22c55e'
                }} data-id="element-239" />
                </div>
                <div data-id="element-240">
                  <p className="text-xs uppercase tracking-[0.3em]" style={{
                  color: '#86efac'
                }} data-id="element-241">
                    {t('competitorsPage.incumbentTag')}
                  </p>
                  <p className="text-xs uppercase tracking-widest mt-1" style={{
                  color: 'rgba(232, 245, 233, 0.5)'
                }} data-id="element-242">
                    {t('competitorsPage.incumbentSubTag')}
                  </p>
                </div>
              </div>

              <div className="grid md:grid-cols-[1fr_auto] gap-6 md:gap-12 items-start" data-id="element-243">
                <div data-id="element-244">
                  <h2 className="font-serif text-3xl md:text-4xl mb-4" style={{
                  color: '#e8f5e9'
                }} data-id="element-245">
                    {t('competitorsPage.incumbentName')}
                  </h2>
                  <p className="leading-relaxed" style={{
                  color: 'rgba(232, 245, 233, 0.75)'
                }} data-id="element-246">
                    {t('competitorsPage.incumbentDesc')}
                  </p>
                </div>
                <div className="rounded-2xl p-6 text-center min-w-[180px] border" style={{
                backgroundColor: 'rgba(0, 0, 0, 0.25)',
                borderColor: 'rgba(34, 197, 94, 0.3)'
              }} data-id="element-247">
                  <p className="text-xs uppercase tracking-widest mb-2" style={{
                  color: '#86efac'
                }} data-id="element-248">
                    {t('competitorsPage.incumbentRevLabel')}
                  </p>
                  <p className="font-serif text-5xl" style={{
                  color: '#22c55e'
                }} data-id="element-249">
                    {t('competitorsPage.incumbentRevVal')}
                  </p>
                  <p className="text-xs mt-2" style={{
                  color: 'rgba(232, 245, 233, 0.5)'
                }} data-id="element-250">
                    {t('competitorsPage.incumbentRevSub')}
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Section divider */}
        <section className="px-6 py-12" data-id="element-251">
          <div className="max-w-5xl mx-auto flex items-center gap-4" data-id="element-252">
            <div className="h-px flex-1" style={{
            backgroundColor: 'rgba(232, 245, 233, 0.15)'
          }} data-id="element-253" />
            <span className="text-xs uppercase tracking-[0.4em]" style={{
            color: '#86efac'
          }} data-id="element-254">
              {t('competitorsPage.top7Tag')}
            </span>
            <div className="h-px flex-1" style={{
            backgroundColor: 'rgba(232, 245, 233, 0.15)'
          }} data-id="element-255" />
          </div>
        </section>

        {/* Competitors */}
        <section className="px-6 pb-16" data-id="element-256">
          <div className="max-w-5xl mx-auto space-y-4" data-id="element-257">
            {competitors.map((c, i) => <motion.article key={c.name} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-50px'
          }} transition={{
            duration: 0.5,
            delay: i * 0.05
          }} className="rounded-2xl p-6 md:p-8 border backdrop-blur-sm" style={{
            backgroundColor: 'rgba(15, 40, 25, 0.5)',
            borderColor: 'rgba(232, 245, 233, 0.1)'
          }} data-id="element-258">
                <div className="flex flex-col md:flex-row md:items-center gap-6" data-id="element-259">
                  <div className="w-14 h-14 rounded-full flex items-center justify-center flex-shrink-0 font-serif text-2xl" style={{
                backgroundColor: 'rgba(34, 197, 94, 0.15)',
                color: '#86efac'
              }} data-id="element-260">
                    {i + 1}
                  </div>

                  <div className="flex-1" data-id="element-261">
                    <h3 className="font-serif text-2xl md:text-3xl mb-2" style={{
                  color: '#e8f5e9'
                }} data-id="element-262">
                      {c.name}
                    </h3>
                    <p className="leading-relaxed text-sm md:text-base" style={{
                  color: 'rgba(232, 245, 233, 0.7)'
                }} data-id="element-263">
                      {c.desc}
                    </p>
                  </div>

                  <div className="md:text-right md:min-w-[200px] pt-3 md:pt-0 border-t md:border-t-0 md:border-l md:pl-6" style={{
                borderColor: 'rgba(232, 245, 233, 0.1)'
              }} data-id="element-264">
                    <p className="text-xs uppercase tracking-widest mb-1" style={{
                  color: '#86efac'
                }} data-id="element-265">
                      {t('competitorsPage.estRevLabel')}
                    </p>
                    <p className="font-serif text-2xl" style={{
                  color: '#22c55e'
                }} data-id="element-266">
                      {c.rev}
                    </p>
                  </div>
                </div>
              </motion.article>)}
          </div>
        </section>

        {/* Bottom line */}
        <section className="px-6 py-20" data-id="element-267">
          <div className="max-w-3xl mx-auto" data-id="element-268">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.7
          }} className="rounded-3xl p-10 md:p-14 text-center border" style={{
            backgroundColor: 'rgba(20, 60, 35, 0.6)',
            borderColor: 'rgba(134, 239, 172, 0.3)'
          }} data-id="element-269">
              <DollarSign className="w-8 h-8 mx-auto mb-6" style={{
              color: '#86efac'
            }} data-id="element-270" />
              <p className="text-xs uppercase tracking-[0.4em] mb-4" style={{
              color: '#86efac'
            }} data-id="element-271">
                {t('competitorsPage.bottomTag')}
              </p>
              <p className="font-serif italic text-2xl md:text-3xl leading-snug mb-4" style={{
              color: '#e8f5e9'
            }} data-id="element-272">
                {t('competitorsPage.bottomTitleStart')}
                <span style={{
                color: '#22c55e'
              }} data-id="element-273">
                  {t('competitorsPage.bottomTitleHighlight')}
                </span>{' '}
                {t('competitorsPage.bottomTitleEnd')}
              </p>
              <p className="text-base leading-relaxed" style={{
              color: 'rgba(232, 245, 233, 0.65)'
            }} data-id="element-274">
                {t('competitorsPage.bottomDescStart')}
                <span style={{
                color: '#e8f5e9',
                fontWeight: 500
              }} data-id="element-275">
                  {t('competitorsPage.bottomDescHighlight')}
                </span>{' '}
                {t('competitorsPage.bottomDescEnd')}
              </p>
            </motion.div>
          </div>
        </section>

        {/* Footer */}
        <footer className="px-6 py-10 border-t" style={{
        borderColor: 'rgba(232, 245, 233, 0.1)'
      }} data-id="element-276">
          <div className="max-w-5xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4 text-sm" data-id="element-277">
            <Link to="/" className="inline-flex items-center gap-2 hover:opacity-80 transition-opacity" style={{
            color: '#e8f5e9'
          }} data-id="element-278">
              <ArrowLeft className="w-4 h-4" data-id="element-279" />
              {t('common.backToMainStage')}
            </Link>
            <span style={{
            color: 'rgba(232, 245, 233, 0.5)'
          }} data-id="element-280">
              {t('competitorsPage.footerTag')}
            </span>
          </div>
        </footer>
      </div>
    </div>;
}